import React, { useEffect, useState } from 'react';
import { CustomerPage } from './CustomerPage';
import { CustomerType, IOrder, IVehicleConfiguration } from 'next-common';
import { IVehiclePageProps, useDisableScroll } from '../../../../../common';
import { useAppSelector } from '@store/hooks';
import { selectBosCustomer } from '@store/selectors';
import { IconType, Icon, CustomModal } from '@repo/ui/components';
import { formatOrganizationNumberInput, formatPhoneNumberInput } from 'types';

export interface ICustomerPageProps extends IVehiclePageProps {
  activeStep?: number;
  order?: IOrder;
  vehicle?: IVehicleConfiguration;
  showCustomerPopup?: boolean;
  setShowCustomerPopup?(value: boolean): void;
}

const DefaultSign = () => <>-</>;

export const CustomerInfo = (props: ICustomerPageProps) => {
  let customer = useAppSelector(selectBosCustomer);
  const [updatedOrder, setUpdatedOrder] = useState(null);
  useDisableScroll(props.showCustomerPopup);

  if (!customer) {
    customer = props?.order?.customer?.bosCustomer;
  }

  useEffect(() => {
    setUpdatedOrder(props.order);
  }, [props.order]);

  const getCommonModalProps = () => ({
    onCloseClick: () => props?.setShowCustomerPopup(false),
  });

  const CustomerName = () => {
    if (customer?.customerType === CustomerType[CustomerType.PRIVATE]) {
      return `${customer?.firstName || ''} ${customer?.lastName || ''}`;
    } else {
      return customer?.organizationName || '-';
    }
  };

  const custOrgNumber = React.useMemo<React.ReactNode>(() => {
    if (customer?.customerType === CustomerType[CustomerType.BUSINESS]) {
      return (
        <div className="flex gap-2 items-center">
          <Icon icon={IconType.NewNextTagSolid} height={24} width={24} fill="var(--color-white)" />
          <span className="text-white text-base/[175%] font-semibold">
            {formatOrganizationNumberInput(customer?.organizationNo)?.formatted ?? <DefaultSign />}
          </span>
        </div>
      );
    }
    return null;
  }, [customer]);

  const customerAddress = () => {
    return [
      customer?.address1?.trim(),
      customer?.address2?.trim(),
      `${customer?.zipCode ?? ''} ${customer?.city ?? ''}`.trim(),
    ]
      .filter(Boolean)
      .join(', ');
  };

  const custRoleIcon = React.useMemo<React.ReactNode>(() => {
    if (customer?.customerType === CustomerType[CustomerType.BUSINESS]) {
      return <Icon icon={IconType.Building} height={24} width={24} fill="var(--color-white)" />;
    } else if (customer?.customerType === CustomerType[CustomerType.PRIVATE]) {
      return <Icon icon={IconType.NewNextUserCustomer} height={24} width={24} fill="var(--color-white)" />;
    }
    return <Icon icon={IconType.NewNextUserCustomer} height={24} width={24} fill="var(--color-white)" />;
  }, [customer]);

  return (
    <>
      <div className="flex items-center profile-left gap-18">
        <div className="flex gap-2 items-center">
          {custRoleIcon}
          <label className="text-white text-lg/[175%] font-semibold">
            {customer ? <CustomerName /> : <DefaultSign />}
          </label>
        </div>
        {custOrgNumber}
        <div className="flex gap-2 items-center">
          <Icon icon={IconType.NewNextPinSolid} height={24} width={24} fill="var(--color-white)" />
          <label className="text-white text-base/[175%] font-semibold">
            {customer ? customerAddress() : <DefaultSign />}
          </label>
        </div>
        {customer?.customerType != CustomerType[CustomerType.BUSINESS] && (
          <>
            <div className="flex gap-2 items-center">
              <Icon icon={IconType.NewNextPhoneSolid} height={24} width={24} fill="var(--color-white)" />
              <label className="text-white text-base/[175%] font-semibold">
                {customer?.phoneMobile ? (
                  formatPhoneNumberInput(`+47${customer?.phoneMobile}`, true)?.formatted
                ) : (
                  <DefaultSign />
                )}
              </label>
            </div>
            <div className="flex gap-2 items-center">
              <Icon icon={IconType.NewNextEMailSolid} height={24} width={24} fill="var(--color-white)" />
              <label className="text-white text-base/[175%] font-semibold">
                {customer?.email ? customer?.email : <DefaultSign />}
              </label>
            </div>
          </>
        )}
      </div>
      {props?.showCustomerPopup && (
        <CustomModal
          {...getCommonModalProps()}
          open={props?.showCustomerPopup}
          headerTitle="Rediger kundedata"
          className="w-152"
        >
          <CustomerPage order={updatedOrder} onClose={() => props?.setShowCustomerPopup(false)} />
        </CustomModal>
      )}
    </>
  );
};
